/**
 * useCalendlyConfig Hook
 *
 * Provides Calendly configuration from SFCC site preferences.
 * This hook reads the Calendly-related preferences from site configuration.
 *
 * @see CL-002: SFCC site preferences for Calendly
 * @see CL-003: Build-time Preferences API call
 *
 * SFCC Site Preferences (under custom group "Calendly"):
 * - calendlyEnabled: Boolean - To enable/disable the feature
 * - calendlyInPersonUrl: String - Default https://calendly.com/king-{{calendlyShowroomId}}/design-consultation
 * - calendlyInVideoUrl: String - Default https://calendly.com/king-{{calendlyShowroomId}}/design-consultation-video-45min
 *
 * SFCC Store Custom Attributes:
 * - calendlyShowroomId: String - Store-level ID for Calendly URL generation (e.g., "king-annandale")
 * - consultationBookingEnabled: Boolean - Per-showroom toggle; exposed as c_consultationBookingEnabled in Shopper APIs
 */

import {useMemo} from 'react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

// Default URL templates - these match the TSD specification
const DEFAULT_IN_PERSON_URL = 'https://calendly.com/king-{{calendlyShowroomId}}/design-consultation';
const DEFAULT_VIDEO_URL = 'https://calendly.com/king-{{calendlyShowroomId}}/design-consultation-video-45min';

/**
 * Whether consultation booking is allowed for this store (SFCC custom attribute).
 * Missing attribute defaults to true for backward compatibility with stores not yet updated in BM.
 *
 * @param {Object|null|undefined} store - Store from Shopper Stores API
 * @returns {boolean}
 */
export const isStoreConsultationBookingEnabled = (store) => {
  const v = store?.c_consultationBookingEnabled;

  if (v === false || v === 'false') return false;

  return true;
};

/**
 * Hook to get Calendly configuration from site preferences.
 *
 * @returns {Object} Calendly configuration
 * @returns {boolean} return.enabled - Whether Calendly feature is enabled
 * @returns {string} return.inPersonUrlTemplate - URL template for in-person consultations
 * @returns {string} return.videoUrlTemplate - URL template for video consultations
 * @returns {boolean} return.isConfigured - Whether Calendly is properly configured (enabled + URL templates)
 */
export const useCalendlyConfig = () => {
  const {site} = useMultiSite();

  return useMemo(() => {
    // Read from site preferences
    // These should be set up as SFCC site preferences and exposed to the PWA
    const preferences = site?.preferences || {};

    // Check if Calendly is enabled from site preferences
    // Support both camelCase and SCREAMING_SNAKE_CASE formats
    const enabled = Boolean(
      preferences.calendlyEnabled ??
      false
    );

    // Get URL templates with fallbacks
    const inPersonUrlTemplate =
      preferences.calendlyInPersonUrl ||
      DEFAULT_IN_PERSON_URL;

    const videoUrlTemplate =
      preferences.calendlyInVideoUrl ||
      DEFAULT_VIDEO_URL;

    const isConfigured = enabled && Boolean(inPersonUrlTemplate || videoUrlTemplate);

    return {
      enabled,
      inPersonUrlTemplate,
      videoUrlTemplate,
      isConfigured,
    };
  }, [site?.preferences]);
};

export default useCalendlyConfig;
